/**
 * MonthlyReport — auto-generated monthly snapshot of Fancall KPIs.
 *
 *   • Month picker (defaults to last completed month)
 *   • Field toggles — 13 core metrics always on, optional extras toggleable
 *   • Excel-compatible CSV export (UTF-8 BOM so Excel opens it as a proper
 *     spreadsheet, not a text file)
 *
 * Auto-generation: by default, opening this report renders LAST MONTH'S
 * numbers. The CEO can change the month any time. (A real cron-driven
 * "every 1st of month, email the CEO" lives outside this dashboard.)
 */
import React, { useState, useMemo } from "react";
import { tokensV2 as T } from "./tokens.v2";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 12000;

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => { controller.abort(); reject(new Error(`timeout after ${ms}ms`)); }, ms);
    promise.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
  });
}

function defaultMonth() {
  // Default to the most recent COMPLETED month (last month). Means on May 1
  // the report shows April 2026 by default — the most useful view for a CEO
  // doing month-end review.
  const d = new Date();
  d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(yyyymm) {
  if (!/^\d{4}-\d{2}$/.test(yyyymm)) return yyyymm;
  const d = new Date(`${yyyymm}-01T00:00:00`);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function formatValue(v, format) {
  if (v == null) return "—";
  const num = Number(v);
  if (!Number.isFinite(num)) return "—";
  if (format === "money") {
    if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
    if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
    if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
    return "Rs " + Math.round(num);
  }
  if (format === "rating") return num.toFixed(2) + " / 5";
  if (format === "minutes") return num.toLocaleString("en-IN") + " min";
  return num.toLocaleString("en-IN");
}

function rawCsvValue(v, format) {
  // For the spreadsheet, write the raw number so Excel can do math on it.
  if (v == null) return "";
  const num = Number(v);
  if (!Number.isFinite(num)) return "";
  if (format === "rating") return num.toFixed(2);
  return String(num);
}

function buildCsv(month, fields, fetchedAt) {
  const lines = [];
  // UTF-8 BOM (﻿) makes Excel open the file as Unicode CSV (otherwise
  // it can mis-read currency symbols and Indian numerals).
  lines.push(`Fancall — Monthly Report`);
  lines.push(`Month,${monthLabel(month)}`);
  lines.push(`Generated,${fetchedAt}`);
  lines.push(``);

  // Group rows by section so the spreadsheet reads as a structured report.
  const grouped = {};
  for (const f of fields) {
    if (!grouped[f.group]) grouped[f.group] = [];
    grouped[f.group].push(f);
  }
  const escape = (v) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  for (const [group, rows] of Object.entries(grouped)) {
    lines.push(escape(group));
    lines.push("Metric,Value,Format");
    for (const r of rows) {
      lines.push([escape(r.label), rawCsvValue(r.value, r.format), r.format].join(","));
    }
    lines.push("");
  }
  return "﻿" + lines.join("\n");
}

export function MonthlyReport() {
  const [month, setMonth] = useState(defaultMonth());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extras, setExtras] = useState(new Set()); // optional ids the user has turned on

  const refresh = useMemo(() => async (attempt = 1) => {
    setLoading(true); setError(null);
    const controller = new AbortController();
    try {
      const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
      const apiKey = process.env.REACT_APP_API_KEY;
      const headers = {};
      if (token)       headers.Authorization = `Bearer ${token}`;
      else if (apiKey) headers["X-API-Key"]  = apiKey;
      const params = new URLSearchParams();
      params.set("month", month);
      if (extras.size > 0) {
        // Send core + selected extras as the explicit field list.
        // Core ids come from registry.core flag — we'll include them too.
        const coreIds = (data?.registry || []).filter(f => f.core).map(f => f.id);
        const allIds = Array.from(new Set([...coreIds, ...Array.from(extras)]));
        if (allIds.length > 0) params.set("fields", allIds.join(","));
      }
      const res = await withTimeout(
        fetch(`${API_BASE}/reports/monthly?${params}`, {
          headers,
          signal: controller.signal,
        }),
        FETCH_TIMEOUT_MS, controller,
      );
      // 429 = rate limited. Wait out the limiter window and retry once
      // automatically — the user shouldn't see a "HTTP 429" ever.
      if (res.status === 429 && attempt < 3) {
        const wait = 6000 + (attempt - 1) * 4000; // 6s, then 10s
        await new Promise(r => setTimeout(r, wait));
        return refresh(attempt + 1);
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
      setData(json.data);
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, Array.from(extras).sort().join(",")]);

  // Initial + month-change refresh
  React.useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refresh]);

  const onExport = () => {
    if (!data?.fields) return;
    const csv = buildCsv(data.month, data.fields, data.refreshedAt);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `fancall-monthly-report-${data.month}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Build group → rows for display
  const grouped = useMemo(() => {
    if (!data?.fields) return {};
    const g = {};
    for (const f of data.fields) {
      if (!g[f.group]) g[f.group] = [];
      g[f.group].push(f);
    }
    return g;
  }, [data]);

  const optionalRegistry = (data?.registry || []).filter(f => !f.core);

  const toggleExtra = (id) => {
    const next = new Set(extras);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExtras(next);
  };

  return (
    <div style={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 14, padding: 16, fontFamily: T.font.sans }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text.primary }}>Monthly Report</div>
          <div style={{ fontSize: 12, color: T.text.muted, marginTop: 2 }}>
            Auto-generated from live data · {data?.monthLabel ? `Showing ${data.monthLabel}` : "—"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 11, color: T.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Month</label>
          <input type="month" value={month} max={defaultMonth()} onChange={(e) => setMonth(e.target.value)}
                 style={{ padding: "6px 8px", border: `1px solid ${T.border.subtle}`, borderRadius: 6, fontSize: 12, fontFamily: T.font.sans }} />
          <button type="button" onClick={onExport} disabled={!data?.fields?.length}
                  style={{ padding: "6px 14px", background: T.brand.base, color: "#FFFFFF", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: data?.fields?.length ? "pointer" : "not-allowed", opacity: data?.fields?.length ? 1 : 0.5 }}>
            Export to Excel
          </button>
        </div>
      </div>

      {/* Optional fields toggle pills */}
      {optionalRegistry.length > 0 && (
        <div style={{ background: T.surface.canvas, border: `1px solid ${T.border.subtle}`, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Add more fields (optional)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {optionalRegistry.map(f => {
              const on = extras.has(f.id);
              return (
                <button key={f.id} type="button" onClick={() => toggleExtra(f.id)} style={{
                  padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: on ? T.brand.base : "#FFFFFF",
                  color: on ? "#FFFFFF" : T.text.secondary,
                  border: `1px solid ${on ? T.brand.base : T.border.subtle}`,
                  cursor: "pointer", outline: "none", fontFamily: T.font.sans,
                }}>
                  {on ? "✓ " : "+ "}{f.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(loading || (error && !data)) && (
        // Skeleton placeholder for the metric grid, NEVER a raw error code.
        // If we landed here from a 429 the auto-retry above is already
        // working — show a friendly hint instead of "HTTP 429".
        <div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
            gap: 8, marginBottom: 12,
          }}>
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} style={{
                background: T.surface.canvas,
                border: `1px solid ${T.border.subtle}`,
                borderLeft: `3px solid ${T.brand.soft}`,
                borderRadius: 10, padding: "10px 12px",
                height: 56, opacity: 0.65,
                animation: "fcSheen 1.6s ease-in-out infinite",
                background: `linear-gradient(90deg, ${T.surface.canvas} 0%, rgba(252,128,25,0.10) 50%, ${T.surface.canvas} 100%)`,
                backgroundSize: "200% 100%",
              }} />
            ))}
          </div>
          {error ? (
            <div style={{
              padding: "10px 14px",
              background: T.brand.soft,
              border: `1px solid ${T.brand.base}33`,
              borderRadius: 8, fontSize: 12, color: T.brand.deep,
              textAlign: "center",
            }}>
              {/HTTP 429|rate.?limit|too many/i.test(error)
                ? <>Catching up — too many requests at once. Auto-retrying…</>
                : <>Preparing this month's numbers — this can take a few seconds.</>}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: T.text.muted, textAlign: "center", padding: "6px 0" }}>
              Pulling {monthLabel(month)} data from live sources…
            </div>
          )}
        </div>
      )}

      {data?.fields && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {Object.entries(grouped).map(([group, rows]) => (
            <div key={group}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, padding: "0 4px" }}>
                {group}
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
                gap: 8,
              }}>
                {rows.map(r => (
                  <div key={r.id} style={{
                    background: T.surface.canvas,
                    border: `1px solid ${T.border.subtle}`,
                    borderLeft: `3px solid ${T.brand.base}`,
                    borderRadius: 10, padding: "10px 12px",
                  }}>
                    <div style={{ fontSize: 11, color: T.text.muted, fontWeight: 600, lineHeight: 1.3 }}>{r.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: T.text.primary, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", marginTop: 2 }}>
                      {formatValue(r.value, r.format)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 10, color: T.text.muted, fontStyle: "italic", marginTop: 12, lineHeight: 1.4 }}>
        Source: live SQL on {data?.source?.tables?.length || 0} tables · re-runs every time you change the month.
        Excel export is UTF-8 CSV — opens in Excel/Sheets as a structured spreadsheet (one section per group).
      </div>
    </div>
  );
}
